/**
 * WHAT A BLOCKED ENVELOPE OWES THE CALLER BESIDES A CODE.
 *
 * `blockedReasons` carries a typed code and a sentence. That is enough for a
 * reader and not nearly enough for the loop: a producer that refused an
 * aggregation because the window summed across a dimension it is not additive
 * over ALREADY knows the narrower request it would have accepted, and dropping
 * it at the tool boundary leaves the model to guess its way back — which is
 * exactly how 112 of 117 live Phoenix runs came back with no filters at all
 * (#7929).
 *
 * So the refusal's own particulars and its counter-proposal ride the envelope,
 * in the producer's typed shape, beside the reason strings rather than
 * flattened into them. Nothing here is a figure and nothing here is narration:
 * it is the refused request, described well enough to be re-asked.
 *
 * SERVER-AUTHORED, ALWAYS. These fields are projected from a producer's own
 * typed refusal (`runtime-v3/result-envelope.ts`), never parsed from a model's
 * arguments, so there is no untrusted path onto them.
 */

/**
 * The nearest request the producer WOULD accept.
 *
 * `input` is a PARTIAL request shape — never a value, never an answer. A
 * counter-proposal that carried a figure would be the producer answering a
 * question it just said it could not answer.
 */
export type GovernedToolOutputCounterProposal = Readonly<{
  /** Why this narrower form is offered, in the producer's own words. */
  rationale: string;
  /** The narrowed request, in the same shape the caller sent. */
  input: Readonly<Record<string, unknown>>;
}>;

/** The typed refusal behind a blocked envelope, carried whole. */
export type GovernedToolOutputRefusalCarry = Readonly<{
  /** The producer's closed-vocabulary code, the same string `blockedReasons` leads with. */
  code: string;
  /** Structured particulars: the refs that failed, the members the window held. */
  details: Readonly<Record<string, unknown>>;
  /** Absent when no narrowing exists. Absence is an honest outcome, not a gap. */
  counterProposal?: GovernedToolOutputCounterProposal;
}>;
